import { useNavigate } from 'react-router-dom';
import customersStorageService from 'src/app/customers/services/customers-storage-service';
import projectsStorageService from 'src/app/projects/services/projects-storage-service';
import config from 'src/config';
import {
    AdminViews,
    AppViews,
    EcologyHubViews,
    LoginViews,
    MapViews,
    ProjectViews,
    SamplesViews,
    SurveyViews,
    TableViews,
    SampleManifestViews,
    SampleReceptionViews,
    SampleManifestFormViews,
    HabitatInsightViews,
} from 'src/routes/routesConfig';
import authStorageService from './services/auth-storage-service';

type RouteParams = {
    customerId: string;
    projectId: string;
    view: string;
    samplingEventId: string;
    batchId: string;
};

export const navRoutesConfig = {
    home: '/',
    [AppViews.CUSTOMERS]: '/customers',
    [AppViews.PROJECTS]: (params: { customerId: string }) => `/projects?customerId=${params.customerId}`,
    [AppViews.DASHBOARD]: (params: { projectId: string; customerId: string }) =>
        `/dashboard?projectId=${params.projectId}&customerId=${params.customerId}`,
    [AppViews.UPGRADE_SUBSCRIPTION]: (params: { projectId: string; customerId: string }) =>
        `/upgrade-subscription?projectId=${params.projectId}&customerId=${params.customerId}`,
    [AppViews.ECOLOGY_HUB]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/projectId=${params.projectId}&customerId=${params.customerId}`,
    [MapViews.SPECIES_INSIGHTS_MAP]: (params: { projectId: string; customerId: string }) =>
        `/map/species-insights-map?projectId=${params.projectId}&customerId=${params.customerId}`,
    [MapViews.HABITAT_INSIGHTS_MAP]: (params: { projectId: string; customerId: string }) =>
        `/map/habitat-insights-map?projectId=${params.projectId}&customerId=${params.customerId}`,
    [SurveyViews.SURVEY_ADMIN]: (params: { customerId: string }) => `/survey/survey-admin?customerId=${params.customerId}`,
    [SurveyViews.SURVEY_DESIGN_SUMMARY]: (params: { customerId: string }) =>
        `/survey/survey-design-summary?customerId=${params.customerId}`,
    [SurveyViews.NEW_SURVEY_DESIGN]: (params: { customerId: string }) => `/survey/new-survey-design?customerId=${params.customerId}`,

    [TableViews.SAMPLE_METRICS]: (params: { projectId: string; customerId: string }) =>
        `/tables/sample-metrics?projectId=${params.projectId}&customerId=${params.customerId}`,
    [TableViews.HABITAT_INSIGHTS_METRICS]: (params: { projectId: string; customerId: string }) =>
        `/tables/habitat-insights-metrics?projectId=${params.projectId}&customerId=${params.customerId}`,

    [LoginViews.TERMS_OF_USE]: '/login/terms-of-use',
    [LoginViews.TERMS_OF_USE_DECLINED]: '/login/terms-of-use-declined',
    [LoginViews.IMPERSONATE_USER]: '/login/impersonate-user',

    [AdminViews.USER_MANAGEMENT]: (params: { customerId: string }) => `/admin/user-management?customerId=${params.customerId}`,

    [EcologyHubViews.SPECIES_TRENDS]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/species-trends?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.HABITAT_TRENDS]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/habitat-trends?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.SAMPLE_COMPARISON]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/sample-comparison?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.STATISTICAL_COMPARISON]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/statistical-comparison?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.TAXONOMIC_COMPOSITION]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/taxonomic-composition?projectId=${params.projectId}&customerId=${params.customerId}`,
    [EcologyHubViews.COMMUNITY_COMPARISON]: (params: { projectId: string; customerId: string }) =>
        `/ecology-hub/community-comparison?projectId=${params.projectId}&customerId=${params.customerId}`,

    [ProjectViews.PROJECT_ADMIN]: (params: { customerId: string }) => `/projects/project-admin?customerId=${params.customerId}`,
    [ProjectViews.PROJECT_DEFINITION]: (params: { customerId: string; projectId: string }) =>
        `/projects/project-definition?customerId=${params.customerId}&projectId=${params.projectId}`,
    [ProjectViews.NEW_PROJECT_DEFINITION]: (params: { customerId: string }) =>
        `/projects/new-project-definition?customerId=${params.customerId}`,
    [ProjectViews.FREEMIUM_SPECIES_INSIGHTS]: (params: { customerId: string; projectId: string }) =>
        `/projects/freemium-species-insights?customerId=${params.customerId}&projectId=${params.projectId}`,
    [HabitatInsightViews.HABITAT_INSIGHTS_ADMIN]: (params: { customerId: string }) =>
        `/habitat-insights/admin?customerId=${params.customerId}`,
    [HabitatInsightViews.HABITAT_INSIGHTS_UPLOAD]: (params: { customerId: string; projectId?: string }) =>
        `/habitat-insights/habitat-insights-upload?customerId=${params.customerId}${params.projectId && `&projectId=${params.projectId}`}`,
    [SamplesViews.SAMPLE_MANIFEST]: (params: { projectId: string; customerId: string }) =>
        `/samples/sample-manifest?projectId=${params.projectId}&customerId=${params.customerId}`,
    [SampleManifestViews.FORM]: (params: {
        projectId: string;
        customerId: string;
        view: string;
        samplingEventId: string;
        batchId?: string;
    }) =>
        `/samples/sample-manifest/form?projectId=${params.projectId}&customerId=${params.customerId}&samplingEventId=${
            params.samplingEventId
        }&view=${params.view}${params.batchId ? `&batchId=${params.batchId}` : ''}`,
    [SampleManifestViews.VIEW]: (params: { projectId: string; customerId: string; samplingEventId: string }) =>
        `/samples/sample-manifest/view?projectId=${params.projectId}&customerId=${params.customerId}&samplingEventId=${params.samplingEventId}`,
    [SampleReceptionViews.VIEW_SAMPLE_MANIFEST]: (params: { projectId: string; customerId: string; samplingEventId: string }) =>
        `/samples/sample-reception/view?projectId=${params.projectId}&customerId=${params.customerId}&samplingEventId=${params.samplingEventId}`,
    [SampleReceptionViews.HOME]: `/samples/sample-reception`,
    [SamplesViews.SAMPLE_BATCH_PRINT]: (params: { projectId: string; customerId: string; batchId: string; samplingEventId: string }) =>
        `/samples/sample-batch-print/?projectId=${params.projectId}&customerId=${params.customerId}&batchId=${params.batchId}&samplingEventId=${params.samplingEventId}&printLayout=true`,
};

const useAppNavigation = () => {
    const navigate = useNavigate();
    const back = () => navigate(-1);

    const getCurrentCustomerId = () => customersStorageService.getCurrentCustomerId() || '';
    const getCurrentProjectId = () => projectsStorageService.getCurrentProjectId() || '';

    const getPath = (route: keyof typeof navRoutesConfig, params?: Record<string, string>) => {
        const routerBuilder = navRoutesConfig[route];
        if (typeof routerBuilder === 'string') {
            return routerBuilder;
        }
        return routerBuilder(params as RouteParams);
    };

    const handlers = {
        toHome: () => navigate(navRoutesConfig.home),
        toCustomers: () => navigate(navRoutesConfig.customers),
        toProjects: (params?: { customerId: string }) =>
            navigate(navRoutesConfig.projects({ customerId: params?.customerId || getCurrentCustomerId() })),
        toDashboard: () => navigate(navRoutesConfig.dashboard({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() })),
        toEcologyHub: () => navigate(navRoutesConfig.ecologyHub({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() })),

        toTermsOfUse: () => navigate(navRoutesConfig.termsOfUse),
        toTermsOfUseDeclined: () => navigate(navRoutesConfig.termsOfUseDeclined),
        toImpersonateUser: () => navigate(navRoutesConfig.impersonateUser),

        toSampleMetricsTable: () =>
            navigate(
                navRoutesConfig[TableViews.SAMPLE_METRICS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toHabitatInsightsMetricsTable: () =>
            navigate(
                navRoutesConfig[TableViews.HABITAT_INSIGHTS_METRICS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),

        toSpeciesMap: () =>
            navigate(
                navRoutesConfig[MapViews.SPECIES_INSIGHTS_MAP]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toHabitatMap: () =>
            navigate(
                navRoutesConfig[MapViews.HABITAT_INSIGHTS_MAP]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toUserManagement: () => navigate(navRoutesConfig[AdminViews.USER_MANAGEMENT]({ customerId: getCurrentCustomerId() })),

        toSpeciesTrends: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.SPECIES_TRENDS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toHabitatTrends: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.HABITAT_TRENDS]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toSampleComparison: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.SAMPLE_COMPARISON]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toStatisticalComparison: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.STATISTICAL_COMPARISON]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toTaxonomicComposition: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.TAXONOMIC_COMPOSITION]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),
        toCommunityComparison: () =>
            navigate(
                navRoutesConfig[EcologyHubViews.COMMUNITY_COMPARISON]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),

        // Projects
        toProjectAdmin: () => navigate(navRoutesConfig[ProjectViews.PROJECT_ADMIN]({ customerId: getCurrentCustomerId() })),
        toProjectDefinition: () =>
            navigate(
                navRoutesConfig[ProjectViews.PROJECT_DEFINITION]({ customerId: getCurrentCustomerId(), projectId: getCurrentProjectId() })
            ),
        toNewProjectDefinition: () =>
            navigate(navRoutesConfig[ProjectViews.NEW_PROJECT_DEFINITION]({ customerId: getCurrentCustomerId() })),
        toFreemiumSpeciesInsights: () =>
            navigate(
                navRoutesConfig[ProjectViews.FREEMIUM_SPECIES_INSIGHTS]({
                    customerId: getCurrentCustomerId(),
                    projectId: getCurrentProjectId(),
                })
            ),
        toHabitatInsightsAdmin: () =>
            navigate(navRoutesConfig[HabitatInsightViews.HABITAT_INSIGHTS_ADMIN]({ customerId: getCurrentCustomerId() })),
        toHabitatInsightsUpload: ({ projectId = '' } = {}) =>
            navigate(
                navRoutesConfig[HabitatInsightViews.HABITAT_INSIGHTS_UPLOAD]({
                    customerId: getCurrentCustomerId(),
                    projectId,
                })
            ),
        // Surveys
        toSurveyDesignSummary: () => navigate(navRoutesConfig[SurveyViews.SURVEY_DESIGN_SUMMARY]({ customerId: getCurrentCustomerId() })),
        toNewSurveyDesign: () => navigate(navRoutesConfig[SurveyViews.NEW_SURVEY_DESIGN]({ customerId: getCurrentCustomerId() })),
        toSurveyAdmin: () => navigate(navRoutesConfig[SurveyViews.SURVEY_ADMIN]({ customerId: getCurrentCustomerId() })),

        // Samples
        toSampleManifest: () =>
            navigate(
                navRoutesConfig[SamplesViews.SAMPLE_MANIFEST]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                })
            ),

        toSampleManifestForm: (params: { view?: SampleManifestFormViews; samplingEventId: string; batchId?: string }) =>
            navigate(
                navRoutesConfig[SampleManifestViews.FORM]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                    view: params?.view || SampleManifestFormViews.ADD_FIELD_DATA,
                    samplingEventId: params.samplingEventId,
                    batchId: params.batchId,
                })
            ),
        toSampleManifestView: (params: { samplingEventId: string }) =>
            navigate(
                navRoutesConfig[SampleManifestViews.VIEW]({
                    projectId: getCurrentProjectId(),
                    customerId: getCurrentCustomerId(),
                    samplingEventId: params.samplingEventId,
                })
            ),

        toSampleReception: () => navigate(navRoutesConfig[SampleReceptionViews.HOME]),
        toViewSampleManifest: (params: { projectId: string; customerId: string; samplingEventId: string }) =>
            navigate(
                navRoutesConfig[SampleReceptionViews.VIEW_SAMPLE_MANIFEST]({
                    projectId: params.projectId,
                    customerId: params.customerId,
                    samplingEventId: params.samplingEventId,
                })
            ),
        toLogin: () => {
            const { cognitoLoginUrl, cognitoClientId } = config;
            const locationOrigin = window.location.origin;
            const cognitoRedirectUrl = `${locationOrigin}/oauth?provider=aws`;
            const staticParams = `client_id=${cognitoClientId}&response_type=code&scope=aws.cognito.signin.user.admin`;
            const cognitoUrl = `${cognitoLoginUrl}login?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
            authStorageService.clearAll();
            window.location.href = cognitoUrl;
        },
        toUpgradeSubscription: () =>
            navigate(navRoutesConfig.upgradeSubscription({ projectId: getCurrentProjectId(), customerId: getCurrentCustomerId() })),
        toAddPasskey: () => {
            const { cognitoLoginUrl, cognitoClientId } = config;
            const locationOrigin = window.location.origin;
            const cognitoRedirectUrl = `${locationOrigin}/customers`;
            const staticParams = `client_id=${cognitoClientId}&scope=aws.cognito.signin.user.admin`;
            const cognitoUrl = `${cognitoLoginUrl}passkeys/add?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
            authStorageService.clearAll();
            window.location.href = cognitoUrl;
        },
    };

    return {
        to: navigate,
        getPath,
        back,
        ...handlers,
    };
};

export default useAppNavigation;
